import { nanoid } from 'nanoid';
import { PICKLIST_PLUGIN_ID } from './types';
import type { PicklistPluginElement, ReportPicklistBeforeSubmissionOptions } from './types';
import type { ReportPicklistOption } from 'generated/graphql';
import type { PicklistOption } from 'domains/reporter/Template/usePicklist';
import { Editor, Element, Point, Node, Transforms } from '../../core';
import type { NodeEntry } from '../../core';
import { getMacroEntry } from '../macroPlaceholder';
import type { MacroPlaceholderType, Macro } from '../macroPlaceholder';
import { unreachableCaseLog } from 'types';
import type { SlateContent } from '../../types';
import { replaceNodeAtPath, fragmentToString } from '../../utils';
import { stitchNodesIntoEditor } from '../../stitching';

export const insertPicklist = (): PicklistPluginElement => ({
  // Unique id used to associate the picklist element with its metadata
  picklistID: nanoid(),
  type: PICKLIST_PLUGIN_ID,
  children: [{ text: '' }],
});

export const picklistNodeToText = (node: unknown): string => {
  if (typeof node !== 'object' || node == null) {
    return '';
  }

  if ('text' in node && typeof (node as { text?: unknown }).text === 'string') {
    return ((node as { text?: string }).text ?? '').trim();
  }

  if ('children' in node && Array.isArray((node as { children?: unknown[] }).children)) {
    return ((node as { children: unknown[] }).children ?? [])
      .map((child) => picklistNodeToText(child))
      .filter((value) => value.length > 0)
      .join('');
  }

  return '';
};

export const getPicklistOptionText = (text: unknown): string => {
  if (Array.isArray(text)) {
    return text
      .map((node) => picklistNodeToText(node))
      .filter((value) => value.length > 0)
      .join(' ');
  }

  if (typeof text === 'object' && text != null && 'text' in text) {
    return typeof (text as { text?: unknown }).text === 'string'
      ? ((text as { text?: string }).text ?? '')
      : '';
  }

  return '';
};

export const buildPicklistOptions = (
  options: ReadonlyArray<ReportPicklistOption> | undefined
): ReadonlyArray<PicklistOption> =>
  (options ?? []).flatMap((option, pickIndex) => {
    const optionName = typeof option.name === 'string' ? option.name : '';
    const optionText = getPicklistOptionText(option.text as unknown);
    const resolvedName = optionName.trim().length > 0 ? optionName : optionText;

    return resolvedName.trim().length > 0 ? [{ pickIndex, name: resolvedName }] : [];
  });

export const normalizeWord = (word: string): string =>
  word.replace(/(^[^a-z0-9]+|[^a-z0-9]+$)/gi, '');

/**
 * Normalizes a command token for spoken-command parsing:
 * - lowercases the token
 * - strips boundary punctuation (e.g. `"pick,"` -> `pick`)
 *
 * This is intentionally boundary-only cleanup so inner token characters remain untouched.
 */
export const normalizeCommandToken = (token: string): string =>
  token.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '');

export const normalizeOptionName = (optionName: string): string =>
  optionName.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Normalizes a picklist choice name for dictation matching so that:
 * - Parenthetical numbers like "(0)", "(1)" are removed (user does not say them).
 * - Hyphens are treated as word boundaries so "comet-tail" matches "comet tail".
 * Used when comparing dictated text to option names in parsePickIndexFromOptionNameMatch.
 */
export const normalizeOptionNameForMatching = (optionName: string): string =>
  optionName
    .replace(/\s*\(\d+\)\s*/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Shared spoken-number normalization for picklist command parsing and option-name matching.
export const SPOKEN_NUMBER_WORDS_MAP: Record<string, number> = {
  one: 1,
  won: 1,
  two: 2,
  to: 2,
  too: 2,
  three: 3,
  free: 3,
  tree: 3,
  four: 4,
  for: 4,
  five: 5,
  hive: 5,
  six: 6,
  sicks: 6,
  seven: 7,
  eight: 8,
  ate: 8,
  nine: 9,
  ten: 10,
};

const normalizeNumericToken = (word: string): string => {
  if (/^\d+$/.test(word)) {
    return String(parseInt(word, 10));
  }
  const spokenNumber = SPOKEN_NUMBER_WORDS_MAP[word];
  return spokenNumber != null ? String(spokenNumber) : word;
};

export const normalizeTextToWords = (value: string): string[] =>
  value
    // Normalize case so matching is case-insensitive.
    .toLowerCase()
    // Remove leading/trailing whitespace before tokenization.
    .trim()
    // Split on one-or-more whitespace chars to handle variable spacing.
    .split(/\s+/)
    // Strip non-alphanumeric chars from the start/end of each token.
    .map(normalizeWord)
    // Canonicalize numeric words/digits so "seven or more" and "7 or more" match.
    .map(normalizeNumericToken)
    // Drop empty tokens created by punctuation-only fragments.
    .filter((word) => word.length > 0);

export const getMatchedRawWordLengthFromNormalizedWords = ({
  rawWords,
  matchedNormalizedWordLength,
}: {
  rawWords: string[];
  matchedNormalizedWordLength: number;
}): number => {
  let consumedNormalizedWords = 0;

  for (let i = 0; i < rawWords.length; i++) {
    const normalizedFromRawWord = normalizeTextToWords(
      normalizeOptionNameForMatching(rawWords[i] ?? '')
    );

    consumedNormalizedWords += normalizedFromRawWord.length;
    if (consumedNormalizedWords >= matchedNormalizedWordLength) {
      return i + 1;
    }
  }

  // Fallback keeps previous behavior when mapping cannot be resolved.
  return matchedNormalizedWordLength;
};

export const extractMatchedTextFromPrePost = ({
  text,
  preMatchText,
  postMatchText,
}: {
  text: string;
  preMatchText: string;
  postMatchText: string;
}): string => {
  let remainingText = text;

  if (preMatchText !== '' && remainingText.toLowerCase().startsWith(preMatchText.toLowerCase())) {
    remainingText = remainingText.slice(preMatchText.length);
  }

  if (postMatchText !== '' && remainingText.toLowerCase().endsWith(postMatchText.toLowerCase())) {
    remainingText = remainingText.slice(0, remainingText.length - postMatchText.length);
  }

  return remainingText.trim();
};

type PicklistSelectionMatchLike = {
  preMatchText: string;
  postMatchText: string;
  pickIndex: number;
};

/**
 * Attempts advisor matching against the full transcript, then retries from the latest
 * command-like segment (e.g. last `pick/choose/option ...`) to avoid stale prefix bias.
 *
 * Usage notes:
 * - Keeps existing behavior when no later command segment is present.
 * - Preserves absolute `preMatchText` offsets by stitching the skipped prefix back onto
 *   the segment-level match before returning.
 */
export const resolveAdvisorCommandSegmentMatch = ({
  normalizedAdvisorText,
  picklistId,
  allowTriggerlessOptionNameMatch,
  commandWordSet,
  commandAliasSet,
  getPicklistOptionSelectionMatch,
}: {
  normalizedAdvisorText: string;
  picklistId: string;
  allowTriggerlessOptionNameMatch?: boolean;
  commandWordSet: ReadonlySet<string>;
  commandAliasSet: ReadonlySet<string>;
  getPicklistOptionSelectionMatch: (
    text: string,
    options?: {
      picklistId?: string;
      allowTriggerlessOptionNameMatch?: boolean;
    }
  ) => Nullable<PicklistSelectionMatchLike>;
}): Nullable<PicklistSelectionMatchLike> => {
  const fullTextMatch = getPicklistOptionSelectionMatch(normalizedAdvisorText, {
    picklistId,
    allowTriggerlessOptionNameMatch,
  });

  const tokens = normalizedAdvisorText.split(/\s+/).filter(Boolean);
  const lastCommandTokenIndex = tokens.reduce((latestIndex, token, index) => {
    const normalizedToken = normalizeCommandToken(token);
    const isPicklistCommandToken =
      commandWordSet.has(normalizedToken) || commandAliasSet.has(normalizedToken);
    const hasArgumentAfterToken = index < tokens.length - 1;
    return isPicklistCommandToken && hasArgumentAfterToken ? index : latestIndex;
  }, -1);

  if (lastCommandTokenIndex < 0) {
    return fullTextMatch;
  }

  const latestCommandSegmentTokens = tokens.slice(lastCommandTokenIndex);
  const latestCommandSegment = latestCommandSegmentTokens.join(' ');
  let latestSegmentMatch = getPicklistOptionSelectionMatch(latestCommandSegment, {
    picklistId,
    allowTriggerlessOptionNameMatch,
  });

  // Expected behavior: if speech starts with an explicit command (e.g. "pick ..."),
  // that explicit command should win. A later trigger-less phrase in the same utterance
  // (e.g. "... second option") must not become the selected command.
  //
  // To enforce that, when the first pass is missing or not start-anchored, we retry
  // shorter prefixes from the beginning and take the first start-anchored match.
  // Any remaining words are preserved in postMatchText.
  const firstToken = normalizeCommandToken(tokens[0] ?? '');
  const startsWithExplicitCommand =
    (commandWordSet.has(firstToken) || commandAliasSet.has(firstToken)) && tokens.length > 1;
  if (
    startsWithExplicitCommand &&
    (latestSegmentMatch == null || latestSegmentMatch.preMatchText.trim() !== '')
  ) {
    for (let end = latestCommandSegmentTokens.length - 1; end >= 2; end--) {
      const prefixText = tokens.slice(0, end).join(' ');
      const prefixMatch = getPicklistOptionSelectionMatch(prefixText, {
        picklistId,
        allowTriggerlessOptionNameMatch,
      });

      if (prefixMatch == null || prefixMatch.preMatchText.trim() !== '') {
        continue;
      }

      const truncatedSuffix = tokens.slice(end).join(' ');
      const combinedPostMatchText = [prefixMatch.postMatchText, truncatedSuffix]
        .filter((value) => value.trim() !== '')
        .join(' ')
        .trim();

      latestSegmentMatch = {
        ...prefixMatch,
        postMatchText: combinedPostMatchText,
      };
      break;
    }
  }

  if (latestSegmentMatch == null) {
    return fullTextMatch;
  }

  const leadingTokensText = tokens.slice(0, lastCommandTokenIndex).join(' ');
  const preMatchText = leadingTokensText.length
    ? `${leadingTokensText} ${latestSegmentMatch.preMatchText}`.trim()
    : latestSegmentMatch.preMatchText;

  return {
    ...latestSegmentMatch,
    preMatchText,
  };
};

export const getPreAndPostTextFromWordRange = ({
  text,
  matchStartWordIndex,
  matchEndWordIndexInclusive,
}: {
  text: string;
  matchStartWordIndex: number;
  matchEndWordIndexInclusive: number;
}): {
  preMatchText: string;
  postMatchText: string;
} => {
  const wordSpans = Array.from(text.matchAll(/\S+/g));
  const matchStart = wordSpans[matchStartWordIndex];
  const matchEnd = wordSpans[matchEndWordIndexInclusive];

  if (matchStart == null || matchEnd == null) {
    return { preMatchText: '', postMatchText: '' };
  }

  if (matchStart.index == null || matchEnd.index == null) {
    return { preMatchText: '', postMatchText: '' };
  }

  const preMatchText = text.slice(0, matchStart.index).trim();
  const postMatchText = text.slice(matchEnd.index + matchEnd[0].length);

  return { preMatchText, postMatchText };
};

export const isFullInitialTriggerlessMatch = ({
  preMatchText,
  postMatchText,
}: {
  preMatchText: string;
  postMatchText: string;
}): boolean => preMatchText.trim() === '' && postMatchText.trim() === '';

export const buildPicklistResolutionEmissionKey = ({
  picklistId,
  source,
  pickIndex,
  decisionRule,
  commandMode,
  stablePickText,
  advisorPickText,
}: {
  picklistId?: string | null;
  source: string;
  pickIndex: number;
  decisionRule: string;
  commandMode: string;
  stablePickText: string | null;
  advisorPickText: string | null;
}): string =>
  JSON.stringify([
    picklistId ?? '',
    source,
    pickIndex,
    decisionRule,
    commandMode,
    stablePickText ?? '',
    advisorPickText ?? '',
  ]);

export type OptionNameMatchAnywhere = Readonly<{
  pickIndex: number;
  matchedWordLength: number;
  matchStartWordIndex: number;
  matchedRawWordLength?: number;
  matchStartRawWordIndex?: number;
}>;

/**
 * Finds the first option-name phrase match anywhere in dictated words.
 * Returns where the match starts so callers can split pre/post dictation text.
 */
export const parsePickIndexFromOptionNameMatchAnywhere = ({
  words,
  options,
}: {
  words: string[];
  options: ReadonlyArray<PicklistOption>;
}): OptionNameMatchAnywhere | null => {
  const normalizedWords: string[] = [];
  const normalizedToRawWordIndex: number[] = [];

  words.forEach((rawWord, rawWordIndex) => {
    const expandedWords = normalizeTextToWords(normalizeOptionNameForMatching(rawWord));
    expandedWords.forEach((expandedWord) => {
      normalizedWords.push(expandedWord);
      normalizedToRawWordIndex.push(rawWordIndex);
    });
  });

  if (normalizedWords.length === 0) {
    return null;
  }

  const optionVariants = options
    .map((option) => ({
      pickIndex: option.pickIndex,
      words: normalizeTextToWords(normalizeOptionNameForMatching(option.name)),
    }))
    .filter((option) => option.words.length > 0);

  // Prefer longest option phrase first.
  const sortedVariants = optionVariants.sort((a, b) => b.words.length - a.words.length);

  for (let startIndex = 0; startIndex < normalizedWords.length; startIndex++) {
    const matchedOption = sortedVariants.find((option) =>
      option.words.every((word, idx) => normalizedWords[startIndex + idx] === word)
    );

    if (matchedOption != null) {
      const normalizedEndIndex = startIndex + matchedOption.words.length - 1;
      const matchStartRawWordIndex = normalizedToRawWordIndex[startIndex];
      const matchEndRawWordIndex = normalizedToRawWordIndex[normalizedEndIndex];

      return {
        pickIndex: matchedOption.pickIndex,
        matchedWordLength: matchedOption.words.length,
        matchStartWordIndex: startIndex,
        matchStartRawWordIndex,
        matchedRawWordLength:
          matchStartRawWordIndex != null && matchEndRawWordIndex != null
            ? matchEndRawWordIndex - matchStartRawWordIndex + 1
            : undefined,
      };
    }
  }

  return null;
};

export const getPicklistEndPointById = (
  editor: Editor,
  picklistId: string | null
): Point | null => {
  if (picklistId == null) return null;

  const targetPicklistEntry = Editor.nodes(editor, {
    at: [],
    match: (n) =>
      Element.isElement(n) &&
      n.type === PICKLIST_PLUGIN_ID &&
      (n as PicklistPluginElement).picklistID === picklistId,
  }).next().value as NodeEntry<PicklistPluginElement> | undefined;

  if (targetPicklistEntry == null) return null;

  return Editor.end(editor, targetPicklistEntry[1]);
};

/**
 * Guards trigger-word logic against fuzzy false positives by requiring an explicit
 * command word (e.g. pick/choose/option) or known alias (e.g. PICC/chews).
 */
export const isExplicitPicklistTriggerWord = ({
  word,
  commandWords,
  commandAliases,
}: {
  word: string;
  commandWords: ReadonlyArray<string>;
  commandAliases: ReadonlyArray<string>;
}): boolean => {
  const normalizedWord = normalizeTextToWords(word)[0];
  if (normalizedWord == null) {
    return false;
  }

  const normalizedAliases = commandAliases.map((alias) => alias.toLowerCase());
  return commandWords.includes(normalizedWord) || normalizedAliases.includes(normalizedWord);
};

const getInsertPicklistMacro = (
  editor: Editor,
  picklistEntry: NodeEntry<PicklistPluginElement>,
  picklistContent: SlateContent
): [MacroPlaceholderType, Macro] => {
  const [picklistNode] = picklistEntry;

  const [macroType, macro] = getMacroEntry(editor, picklistContent);

  switch (macroType) {
    case 'plainText':
      return ['plainText', [{ ...picklistNode, children: [{ text: picklistContent }] }]];
    case 'macroText':
      return ['macroText', [{ ...picklistNode, children: [...macro] }]];
    case 'macroInline':
      return ['macroInline', macro];
    case 'macroBlock':
      return ['macroBlock', macro];

    default:
      unreachableCaseLog(macroType);
      throw new Error(`Unsupported macro type: ${String(macroType)}`);
  }
};

// In the report editor, when a picklist is required and touched, the required field indicator is
// displayed and needs to be removed after insertion
export const insertSelectedPicklistItem = (
  editor: Editor,
  picklistEntry: NodeEntry<PicklistPluginElement>,
  picklistContent: SlateContent,
  onInsertNonEmptyPicklist?: () => void,
  options?: {
    readonly selectInsertedContent?: boolean;
  }
): void => {
  Editor.withoutNormalizing(editor, () => {
    const [, content] = getMacroEntry(editor, picklistContent);
    const [macroType, normalizedMacro] = getInsertPicklistMacro(
      editor,
      picklistEntry,
      picklistContent
    );

    const [, picklistPath] = picklistEntry;

    const stitchPicklistContent = () => {
      Transforms.delete(editor, { at: Editor.range(editor, picklistPath) });

      stitchNodesIntoEditor(editor, content, {
        at: Editor.range(editor, picklistPath),
        select: options?.selectInsertedContent ?? true,
      });
    };

    switch (macroType) {
      case 'plainText':
      case 'macroText':
        replaceNodeAtPath(editor, normalizedMacro, { at: picklistPath });
        break;

      case 'macroInline':
      case 'macroBlock':
        stitchPicklistContent();
        break;

      default:
        unreachableCaseLog(macroType);
    }

    // handle required fields
    if (
      onInsertNonEmptyPicklist !== undefined &&
      normalizedMacro.length === 1 &&
      normalizedMacro[0].children[0].text !== ''
    ) {
      onInsertNonEmptyPicklist();
    }
  });
};

export const getPicklistOptionDisplayName = (
  option: ReportPicklistBeforeSubmissionOptions
): string => {
  const picklistContent = option.text as SlateContent;
  return option.name != null ? option.name : fragmentToString(picklistContent);
};

export const cloneNodeFragment = (fragment: ReadonlyArray<Node>): Node[] =>
  fragment.map((node) => ({ ...node }));

export const asPicklistOptionText = (
  value: Node[]
): ReportPicklistBeforeSubmissionOptions['text'] =>
  value as unknown as ReportPicklistBeforeSubmissionOptions['text'];

export const picklistTextToNodes = (
  text: ReportPicklistBeforeSubmissionOptions['text']
): Node[] | null => (Array.isArray(text) ? (text as unknown as Node[]) : null);

export const isBlankPicklistOptionText = (
  text: ReportPicklistBeforeSubmissionOptions['text'],
  blankValue: string
): boolean => {
  const nodes = picklistTextToNodes(text);
  if (nodes == null || nodes.length === 0) return false;

  const firstNode = nodes[0] as { text?: string };
  return typeof firstNode?.text === 'string' ? firstNode.text.trim() === blankValue.trim() : false;
};

export const picklistTextToEditableFragment = (
  text: ReportPicklistBeforeSubmissionOptions['text'] | undefined,
  blankFragment: ReadonlyArray<Node>
): Node[] => {
  if (text === undefined) {
    return cloneNodeFragment(blankFragment);
  }

  const nodes = picklistTextToNodes(text);
  return nodes != null ? nodes.map((node) => ({ ...node })) : cloneNodeFragment(blankFragment);
};

/**
 * Returns all picklist nodes from the parent node, including nested picklists.
 */
export const getPicklistsNodesFromParentNode = (root: Node): PicklistPluginElement[] => {
  const nodes = Node.nodes(root);

  const picklistNodes: Array<PicklistPluginElement> = [];
  for (const [picklistNode] of Array.from(nodes)) {
    if (Element.isElement(picklistNode) && picklistNode.type === PICKLIST_PLUGIN_ID) {
      picklistNodes.push({
        picklistID: picklistNode.picklistID,
        children: picklistNode.children,
        type: PICKLIST_PLUGIN_ID,
      });
    }
  }

  return picklistNodes;
};
